<template>
	<div class="page repo-detail-page">
		<v-breadcrumbs
			v-if="!repositoryMissing"
			:items="breadcrumbs"
			class="sgnl-p-0 sgnl-m-0"
		/>
		<section
			v-if="!repositoryMissing"
			class="repo-detail-grid"
		>
			<div class="grid-repoCard">
				<ConnectedRepositoryCard
					v-if="repositoryID && repository"
					:repositoryID="repositoryID"
					:repository="repository"
					ref="connectedRepositoryCard"
					:uiRepositoryDetailMode="true"
					:initialBuildBarStatus="true"
					@open-execution-log="repositoryExecutionResultsHandler"
					:variableTraceList="repositoryVariableTraceList"
					@repository-variables-updated="updateVariables"
					@repository-missing="repositoryMissing = true"
				/>
			</div>

			<div class="grid-variableViewer">
				<VariableViewer
					id="variableViewer"
					ref="variableViewer"
					:disableRequestPanelClose="true"
					:variablesList="completeVariablesList"
					:referenceCoreChosen="referenceCoreChosen"
					:variableDiscoveryInProgress="variableDiscoveryInProgress"
					:currentCodeHash="repositoryHash"
					@discover-variables="discoverVariables"
					@update-variable-trace-list="updateVariableTraceList"
					@switch-to-ref-core="switchToReferenceCore"
					@add-custom-trace-expression="addCustomTraceExpression"
					@remove-custom-trace-expression="removeCustomTraceExpression"
				/>
			</div>

			<div class="grid-resultsPanel">
				<Panel
					:taskID="taskID"
					:buildID="buildID"
					:showCopyButton="true"
				/>
			</div>
		</section>
		<v-card
			v-else
			:border="false"
			class="empty-state-card"
		>
			<img
				src="@/assets/icons/common/repository-not-found-96.svg"
				alt=""
				width="96"
				height="96"
			/>
			<v-card-title class="sgnl-h6-semibold sgnl-text-color-default"> Repository not found </v-card-title>
			<v-card-text class="empty-state-card__text">
				<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
					This repository is no longer connected to your account.
				</div>
				<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
					It may have been disconnected from another session. Return to your repositories to reconnect it or
					choose another.
				</div>
			</v-card-text>
			<v-card-actions>
				<v-btn
					variant="flat"
					color="primary"
					:to="{ name: 'Repositories' }"
				>
					Back to repositories
				</v-btn>
			</v-card-actions>
		</v-card>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

// Types
import { TraceVariable, TraceVariableWithTraceType } from "@/types/api/nodes";

// Libraries
import axios from "axios";

// Utilities
import * as util from "@/js/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";

// Components
import ConnectedRepositoryCard from "@/components/Repositories/ConnectedRepositoryCard.vue";
import VariableViewer from "@/components/DevelopmentPlatform/VariableViewer.vue";
import Panel from "@/components/Panel.vue";

// Stores
import { useRepositoriesStore } from "@/stores/repositories";
import { useGithubStore } from "@/stores/github";

// Global variables
import { defaultTabs } from "@/assets/defaultTabs";
import { ResultsPanelTab } from "@/types/general";
import { useOutputsStore } from "@/stores/outputs";
import { Repository } from "@/types/api/repositories";
import { storeToRefs } from "pinia";

type ComponentData = {
	results: null | ResultsPanelTab[];
	resultsLoading: boolean;
	breadcrumbs: any[];
	discoveredVariables: TraceVariableWithTraceType[];
	customVariableExpressions: TraceVariableWithTraceType[];
	repositoryVariableTraceList: TraceVariable[];
	variableDiscoveryInProgress: boolean;
	repositoryMissing: boolean;
};

export default defineComponent({
	name: "RepositoryDetail",
	components: { ConnectedRepositoryCard, VariableViewer, Panel },
	props: {
		repositoryID: { type: String },
	},
	data: (): ComponentData => ({
		results: null,
		resultsLoading: false,
		breadcrumbs: [
			{
				title: "Repositories",
				disabled: false,
				exact: true,
				to: { name: "Repositories" },
			},
			{
				title: "" /* Gets populated in created() */,
				disabled: true,
			},
		],
		discoveredVariables: [],
		customVariableExpressions: [],
		repositoryVariableTraceList: [],
		variableDiscoveryInProgress: false,
		repositoryMissing: false,
	}),
	setup() {
		const repositoriesStore = useRepositoriesStore();
		const githubStore = useGithubStore();
		const outputStore = useOutputsStore();
		/*
		 *	https://stackoverflow.com/a/71677026/7151170
		 *	Ideally we would have a direct reference to the repository we want and not to the list.
		 */
		const { connectedRepositories } = storeToRefs(repositoriesStore);

		const connectedRepositoryCard = ref<InstanceType<typeof ConnectedRepositoryCard>>();
		return {
			repositoriesStore,
			githubStore,
			connectedRepositoryCard,
			util,
			outputStore,
			connectedRepositories,
		};
	},
	computed: {
		repository(): Repository | undefined {
			if (!this.repositoryID || !this.connectedRepositories) {
				return undefined;
			}
			return this.connectedRepositories.find((r) => r.RepositoryID === this.repositoryID);
		},
		buildID(): string {
			// Try to get BuildID from activeTask first, then fall back to activeBuild
			// This ensures we get build output even when build fails without creating a task
			return (
				this.connectedRepositoryCard?.activeTask?.BuildID ??
				this.connectedRepositoryCard?.activeBuild?.BuildID ??
				""
			);
		},
		taskID(): string {
			return this.connectedRepositoryCard?.activeTask?.TaskID ?? "";
		},
		referenceCoreChosen(): boolean {
			if (this.connectedRepositoryCard?.chosenCore) {
				return this.connectedRepositoryCard?.chosenCore.Microarchitecture === "Reference";
			}
			return false;
		},
		repositoryHash(): string | undefined {
			return (
				(this.connectedRepositoryCard?.repository?.Commit ?? "") +
				(this.connectedRepositoryCard?.repository?.BuildDirectory ?? "")
			);
		},
		completeVariablesList(): any[] {
			return [...this.discoveredVariables, ...this.customVariableExpressions];
		},
		resultsPanelText(): readonly ResultsPanelTab[] {
			return this.results ?? defaultTabs;
		},
	},
	methods: {
		async repositoryExecutionResultsHandler(data: { taskID: string; buildID: string }) {
			if (data.taskID && !this.connectedRepositoryCard?.lastBuildForVariableDiscovery) {
				this.resultsLoading = true;
				try {
					await this.outputStore.loadTaskOutputs(data.taskID);
					await this.outputStore.loadBuildOutputs(this.buildID);
				} catch {
					// Access state populated in store; BuildBar surfaces.
				}
			}
		},
		async discoverVariables() {
			if (!this.connectedRepositoryCard) {
				return;
			}
			this.variableDiscoveryInProgress = true;
			try {
				await this.connectedRepositoryCard.discoverVariables();
			} catch (error) {
				monitoringCaptureError(error, "Discover repository variables");
			}
		},
		updateVariableTraceList(newList) {
			this.repositoryVariableTraceList = newList;
		},
		updateVariables(newVariables) {
			this.discoveredVariables = newVariables;
			this.variableDiscoveryInProgress = false;
		},
		switchToReferenceCore() {
			this.connectedRepositoryCard?.switchToReferenceCore();
		},
		addCustomTraceExpression(variableTrace) {
			this.customVariableExpressions.push(variableTrace);
		},
		removeCustomTraceExpression(variableTraceToRemove: TraceVariable) {
			const traceVariableIndex = this.customVariableExpressions.findIndex((variableTrace) => {
				return (
					variableTrace.Expression == variableTraceToRemove.Expression &&
					variableTrace.LineNumber == variableTraceToRemove.LineNumber &&
					variableTrace.File == variableTraceToRemove.File
				);
			});
			this.customVariableExpressions.splice(traceVariableIndex, 1);
		},
	},
	created() {
		this.breadcrumbs[1].title = this.repositoryID;
	},
	async mounted() {
		if (this.repositoryID) {
			try {
				await this.repositoriesStore.fetchRepositoryByID(this.repositoryID);
			} catch (error) {
				if (axios.isAxiosError(error) && error.response?.status === 404) {
					monitoringCaptureError(error, "Repository not found on detail page mount", { severity: "info" });
					this.repositoryMissing = true;
					return;
				}
				monitoringCaptureError(error, "Load repository on mount");
			}
		}
	},
});
</script>

<style lang="scss" scoped>
.repo-detail-page {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.repo-detail-grid {
	min-width: var(--sgnl-container-343);
	display: grid;
	gap: var(--sgnl-space-8);

	grid-template-columns: minmax(300px, 1fr);
	grid-template-rows: auto 250px 1fr;
	grid-template-areas:
		"repoCard"
		"variableViewer"
		"resultsPanel";
}

@media (min-width: map-get($grid-breakpoints,md)) {
	.repo-detail-grid {
		grid-template-columns: minmax(250px, 1fr) minmax(400px, 2fr);
		grid-template-rows: auto 66vh;
		grid-template-areas:
			"repoCard repoCard"
			"variableViewer resultsPanel";
	}
}

.grid-repoCard {
	grid-area: repoCard;
}
.grid-variableViewer {
	grid-area: variableViewer;
}
.grid-resultsPanel {
	grid-area: resultsPanel;
}
</style>
